import type { Ordering } from "@/shared/lib/ordering";

// Real backend fields only. The model has NO region and NO flagged — those columns
// were dropped from the UI (Jaxongir's decision); never invent placeholder columns.

export type CustomerStatus = "active" | "inactive" | "new";

// One row in GET /api/support/customers/
export interface CustomerListItem {
  id: number;
  first_name: string;
  last_name: string;
  phone_number: string;
  status: CustomerStatus;
  loans_count: number;
  last_login: string | null;
  // Latest OTP login (USER_LOGIN ActionLog; falls back to last_login, which OTP never updates).
  last_login_at: string | null;
  // Premium flag shown as a badge in the list/table (detail adds the nested `premium`).
  is_premium: boolean;
}

// Shape of the Premium subscription row returned by the detail endpoint.
export interface CustomerPremium {
  end_date: string;
  is_free: boolean;
  is_repeatable: boolean;
  active: boolean;
}

// Detail from GET /api/support/customers/{id}/ — adds editable identity fields
// plus read-only account/premium metadata.
export interface CustomerDetail extends CustomerListItem {
  pin: string | null;
  passport_number: string | null;
  birth_date: string | null;
  address: string | null;
  // Registration timestamp (User.date_joined) — shown in the drawer header and as the
  // first timeline event. Detail-only: the list table has no registration column.
  date_joined: string;
  // Account / premium fields (read-only in this subtask; write-ops are in 0063.04).
  // `is_premium` is inherited from CustomerListItem.
  balance: string;
  is_developer: boolean;
  free_premiums_count: number;
  premium: CustomerPremium | null;
}

// ── Customer timeline (task 0066) ────────────────────────────────────────────
// One normalized feed from GET /support/customers/{id}/timeline/. Three kinds:
//   registered     — synthetic, from User.date_joined (no operator);
//   status_change  — lifecycle status diffs (field="status", old→new);
//   support_action — an operator action on this customer (carries `action` + `extra`).
export type TimelineKind = "registered" | "status_change" | "support_action";

export interface TimelineEvent {
  kind: TimelineKind;
  timestamp: string;
  actor_id: number | null;
  actor_name: string | null;
  actor_type: string | null;
  action: string | null;
  field: string | null;
  old: string | null;
  new: string | null;
  extra: Record<string, unknown> | null;
}

// DRF page-number pagination envelope.
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Subset of editable fields PATCHed back. Only changed keys are sent.
export interface CustomerUpdate {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  pin?: string;
  passport_number?: string;
  birth_date?: string;
  address?: string;
  status?: CustomerStatus;
}

// Clickable list headers -> SupportCustomerViewSet.ordering_fields. status_order ranks
// NEW < ACTIVE < INACTIVE (the backend default when no ordering is sent).
export type CustomerSortField = "first_name" | "phone_number" | "loans_count" | "status_order" | "last_login_at";
export type CustomerOrdering = Ordering<CustomerSortField>;

export interface CustomerListParams {
  search: string;
  status: CustomerStatus | "all";
  // Omitted / null = backend default (status first, newest registration next).
  ordering?: CustomerOrdering | null;
  page: number;
}

// ── Customer activity (task 0153) ────────────────────────────────────────────
// What the customer did themselves — GET /support/customers/{id}/activity/?period=.
// `last5` is a fixed slice (next/previous always null); `7d` / `all` are paginated.
export type ActivityPeriod = "last5" | "7d" | "all";

export interface ActivityEvent {
  id: string;
  timestamp: string;
  action: string;
  category: string;
  // App the action came from; "" on rows older than the actor_type column.
  app: "merchant" | "client" | "";
  target_ct: string;
  target_id: number | null;
  target_repr: string;
  description: string;
  extra: Record<string, unknown>;
}

// ── Sensitive write-operation request bodies (Faza B — 0063.04) ──────────────
// Each maps 1:1 to a POST /support/customers/{id}/{action}/ endpoint. All three
// echo the full CustomerDetail back on success.

export interface DeveloperToggleBody {
  is_developer: boolean;
}

export interface BalanceAdjustBody {
  // amount is a decimal string to mirror the backend DecimalField (max_digits=10,
  // decimal_places=2) without float rounding. Always positive; `direction` decides sign.
  amount: string;
  direction: "credit" | "debit";
  reason?: string;
  idempotency_key?: string;
}

export interface GrantPremiumBody {
  is_free: boolean;
}

export const PAGE_SIZE = 9;
export const ACTIVITY_PAGE_SIZE = 20;
